import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import type { AutomationTemplate, TemplateGroup } from "@/utils/automation-templates";
import IconChevronRight from "central-icons/IconChevronRight";
import { IconStar } from "central-icons/IconStar";
import type { FC } from "react";

export const AutomationTemplateGroup: FC<{
    group: TemplateGroup;
    index: number;
    onTemplateSelect?: (template: AutomationTemplate) => void;
}> = ({ group, index, onTemplateSelect }) => (
    <div className="flex flex-col gap-4">
        {group.label && (
            <div className="flex flex-col gap-1">
                {index > 0 ? (
                    <Separator label={group.label} />
                ) : (
                    <Text className="text-sm font-semibold text-content-primary">{group.label}</Text>
                )}

                {group.description && <Text className="text-sm text-content-muted">{group.description}</Text>}
            </div>
        )}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {Object.values(group.templates).map((template: AutomationTemplate) => (
                <AutomationTemplateCard
                    template={template}
                    onSelect={onTemplateSelect}
                    key={template.id}
                    interactive={!!onTemplateSelect}
                />
            ))}
        </div>
    </div>
);

const Separator: FC<{ label: string }> = ({ label }) => (
    <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border-base" />
        <Text className="text-xs font-medium uppercase tracking-wider text-content-muted">{label}</Text>
        <div className="h-px flex-1 bg-border-base" />
    </div>
);

const AutomationTemplateCard: FC<{
    template: AutomationTemplate;
    onSelect?: (template: AutomationTemplate) => void;
    interactive: boolean;
}> = ({ template, onSelect, interactive }) => {
    const content = (
        <>
            <div className="flex w-full items-center gap-3">
                <div className={cn("shrink-0 rounded-[4px] p-1", template.iconBgColor, template.iconColor)}>
                    <template.icon size={16} />
                </div>
                <Text className="grow truncate text-sm font-semibold text-content-primary">{template.title}</Text>
                {template.recommended && (
                    <Tooltip content="Recommended" side="top">
                        <span className="shrink-0 text-content-brand">
                            <IconStar size={14} />
                        </span>
                    </Tooltip>
                )}
                {interactive && <IconChevronRight size={16} className="shrink-0 text-content-muted" />}
            </div>
            <Text className="line-clamp-2 text-sm text-content-muted">{template.description}</Text>
            {template.requiredIntegrations && template.requiredIntegrations.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5">
                    {template.requiredIntegrations.map((name) => (
                        <span
                            key={name}
                            className="rounded-md border border-border-base px-2 py-0.5 text-xs text-content-secondary"
                        >
                            {name}
                        </span>
                    ))}
                </div>
            )}
        </>
    );

    if (!interactive) {
        return (
            <div
                className="flex flex-col gap-2 rounded-xl border border-border-base bg-surface-popover p-4 text-left"
                data-testid={`automation-template-card-${template.id}`}
            >
                {content}
            </div>
        );
    }

    return (
        <button
            className={cn(
                "flex flex-col gap-2 rounded-xl border border-border-base bg-surface-popover p-4 text-left transition-[border-color,box-shadow]",
                template.enabled
                    ? "hover:border-border-strong hover:shadow-sm"
                    : "opacity-50 disabled:bg-surface-popover",
            )}
            onClick={() => onSelect?.(template)}
            data-tracking-id={`automation-template-card-select-${template.id}`}
            data-testid={`automation-template-card-${template.id}`}
            disabled={!template.enabled}
        >
            {content}
        </button>
    );
};
